import Sortable from "sortablejs";
import { patch } from "@rails/request.js";
import { onceShown } from "./compose_helpers.js";

// The card is replaced after every change, so focus has to outlive the element that
// held it. Controls name themselves with data-card-focus; a menu item names its
// row's Actions button, because the menu goes away with the card.
let pendingFocus = null;

export function readLayout(root) {
  const sections = [...root.querySelectorAll("[data-section]")].map((el) => el.dataset.section);
  const rows = {};
  root.querySelectorAll("[data-section-rows]").forEach((list) => {
    rows[list.dataset.sectionRows] = [...list.querySelectorAll("[data-line-id]")].map((row) => row.dataset.lineId);
  });
  return { sections, rows };
}

// Nothing to save until both limits are in, and unticking clears them.
export function limitsPayload({ limited, min, max }) {
  if (!limited) return { line_item: { customer_quantity: { min: "", max: "" } } };
  if (min === "" || max === "") return null;
  return { line_item: { customer_quantity: { min, max } } };
}

// A button can end up disabled by its own move, at the top or bottom of the list, and
// a limit field hidden when the card comes back without the limits it was refused.
export function focusTarget(root, key) {
  const find = (name) => root.querySelector(`[data-card-focus="${name}"]`);
  const focusable = (element) => !element.disabled && element.getClientRects().length > 0;
  const element = find(key);
  if (!element || focusable(element)) return element;
  const fallback = find(element.dataset.cardFocusFallback);
  return fallback && focusable(fallback) ? fallback : find("add-group");
}

// Opens on the side the menu fits, or else the roomier one, and never past the
// window: the height it cannot have scrolls inside it.
export function menuPlacement({ trigger, height, viewportHeight, gap = 4, margin = 8 }) {
  const below = viewportHeight - trigger.bottom - gap - margin;
  const above = trigger.top - gap - margin;
  const opensDown = height <= below || below >= above;
  const maxHeight = Math.max(opensDown ? below : above, 0);
  const top = opensDown ? trigger.bottom + gap : trigger.top - gap - Math.min(height, maxHeight);
  return { top, maxHeight };
}

export function nextMenuItem(items, current, key) {
  const index = items.indexOf(current);
  switch (key) {
    case "ArrowDown":
      return items[(index + 1) % items.length];
    case "ArrowUp":
      return items[(index - 1 + items.length) % items.length];
    case "Home":
      return items[0];
    case "End":
      return items[items.length - 1];
    default:
      return null;
  }
}

// Turbo disables a form's submitter while it runs, which takes focus off it, so a
// button is remembered here and everything else as the card is about to go.
function rememberSubmitter(event) {
  const key = event.detail.formSubmission?.submitter?.dataset?.cardFocus;
  if (key) pendingFocus = key;
}

function rememberFocus(event) {
  const target = document.getElementById(event.target.getAttribute("target"));
  if (!target?.matches("[data-products-card]")) return;
  pendingFocus = document.activeElement?.dataset?.cardFocus ?? pendingFocus;
}

export default function registerProductsCard(Alpine) {
  document.addEventListener("turbo:submit-start", rememberSubmitter);
  document.addEventListener("turbo:before-stream-render", rememberFocus);

  Alpine.data("productsCard", ({ layoutUrl }) => ({
    sortables: [],

    init() {
      this.$nextTick(() => {
        this.setupSortable();
        this.restoreFocus();
      });
    },

    destroy() {
      this.sortables.forEach((sortable) => sortable.destroy());
      this.sortables = [];
    },

    setupSortable() {
      const shared = {
        animation: 150,
        forceFallback: true,
        ghostClass: "opacity-30",
        dragClass: "shadow-2xl"
      };

      this.$root.querySelectorAll("[data-section-rows]").forEach((list) => {
        this.sortables.push(
          Sortable.create(list, {
            ...shared,
            group: "products-card-rows",
            handle: "[data-drag-handle]",
            draggable: "[data-line-id]",
            onEnd: (event) => this.onDrop(event)
          })
        );
      });

      // A card with nothing on it has no sections to sort.
      const sections = this.$root.querySelector("[data-sections]");
      if (!sections) return;

      this.sortables.push(
        Sortable.create(sections, {
          ...shared,
          group: "products-card-sections",
          handle: "[data-section-handle]",
          draggable: "[data-section]",
          onEnd: (event) => this.onDrop(event)
        })
      );
    },

    async onDrop(event) {
      if (event.from === event.to && event.oldIndex === event.newIndex) return;

      const saved = await this.save(layoutUrl, readLayout(this.$root));
      if (!saved) event.from.insertBefore(event.item, event.from.children[event.oldIndex] || null);
    },

    saveLimits(url, state) {
      const body = limitsPayload(state);
      return body ? this.save(url, body) : Promise.resolve(false);
    },

    // A 422 comes back with the card and its message. Anything else, like a proposal
    // sent from another tab, has no card to show, so the page starts over.
    async save(url, body) {
      try {
        const response = await patch(url, {
          body: JSON.stringify(body),
          contentType: "application/json",
          responseKind: "turbo-stream"
        });
        if (!response.ok && response.statusCode !== 422) window.location.reload();
        return response.ok;
      } catch {
        window.location.reload();
        return false;
      }
    },

    restoreFocus() {
      const key = this.$root.dataset.focus || pendingFocus;
      pendingFocus = null;
      if (key) focusTarget(this.$root, key)?.focus();
    }
  }));

  Alpine.data("productRowMenu", () => ({
    open: false,
    moveOpen: false,
    menuStyle: "",

    toggle() {
      if (this.open) {
        this.close();
        return;
      }
      this.open = true;
      this.$nextTick(() => {
        this.position();
        onceShown(this, () => {
          this.position();
          this.$nextTick(() => this.items()[0]?.focus());
        });
      });
    },

    // Reads the menu's height, so it has to run with the menu showing.
    position() {
      const rect = this.$refs.trigger.getBoundingClientRect();
      const width = 224;
      const left = Math.max(8, rect.right - width);
      const { top, maxHeight } = menuPlacement({
        trigger: rect,
        height: this.$refs.menu.scrollHeight,
        viewportHeight: window.innerHeight
      });
      this.menuStyle = `top:${top}px;left:${left}px;max-height:${maxHeight}px;`;
    },

    close(returnFocus = true) {
      if (!this.open) return;
      this.open = false;
      this.moveOpen = false;
      if (returnFocus) this.$refs.trigger.focus();
    },

    toggleMove() {
      this.moveOpen = !this.moveOpen;
      this.$nextTick(() => {
        this.position();
        this.$nextTick(() => {
          if (this.moveOpen) this.$refs.moveTargets?.scrollIntoView({ block: "nearest" });
        });
      });
    },

    items() {
      return [...this.$refs.menu.querySelectorAll('[role="menuitem"]:not([disabled])')].filter(
        (item) => item.offsetParent !== null
      );
    },

    onKey(event) {
      if (!this.open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key === "Tab") {
        this.close();
        return;
      }
      const item = nextMenuItem(this.items(), document.activeElement, event.key);
      if (item) {
        event.preventDefault();
        item.focus();
      }
    }
  }));
}
